import queryClient from "api/queryClient.ts";
import { useGetIntegrationQuery, useUpdateIntegrationMutation } from "api/TenantSlug/Integration/hooks";
import { composeGetIntegrationQueryOptions } from "api/TenantSlug/Integration/queryOptions.ts";
import { useGetIntegrationCustomFieldsQuery } from "api/TenantSlug/IntegrationCustomField/hooks.ts";
import { DiagramWithStorage } from "components/CC/Diagrams/DiagramWithStorage/DiagramWithStorage.tsx";
import { CCEntityType } from "components/CC/types/entityConfig.ts";
import { QueryState } from "components/UI/QueryState/QueryState.tsx";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { useCallback } from "react";
import { PermissionEnum } from "types/enums.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute.ts";
import { getToolScenarioIntegrationHeaderItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute(
    "/_authenticated/$tenantSlug/_layout/tool/_layout/scenario/_layout/integration/_layout/$integrationId/_layout/editor/"
)({
    component: IntegrationEditorPage,
    staticData: {
        permissions: [PermissionEnum.ToolIntegrationEditor],
        showInSideMenu: false,
        label: "pages.tool.scenario.integration.editor.title",
    },
    loader: ({ context: { t } }) => {
        return {
            breadcrumb: t("pages.tool.scenario.integration.editor.title"),
        };
    },
});

function IntegrationEditorPage() {
    const { integrationId } = Route.useParams();

    const {
        data: freshData,
        isLoading: isLoadingFreshIntegration,
        isError: isFreshIntegrationError,
    } = useGetIntegrationQuery(integrationId, { filter: { include: "scenarios,formFields" } }, { staleTime: 0 });
    const { data: integrationCustomFieldsData, isLoading: isLoadingCustomFields } = useGetIntegrationCustomFieldsQuery(
        integrationId,
        { enabled: Boolean(integrationId) }
    );

    const integration = freshData?.integration;
    const scenarioId = integration?.scenarios?.[0]?.id ?? null;
    const customFields = integrationCustomFieldsData?.integrationCustomFields ?? [];

    const updateIntegrationMutation = useUpdateIntegrationMutation(integrationId);

    const onScenarioCreated = useCallback(
        async (newScenarioId: string): Promise<void> => {
            // Fetch fresh data to avoid overriding values changed in the general form
            const freshData = await queryClient.fetchQuery({
                ...composeGetIntegrationQueryOptions(integrationId, {
                    filter: { include: "offers,labels,scenarios,formFields" },
                }),
                staleTime: 0,
            });

            const int = freshData.integration;
            if (!int) {
                throw new Error("Failed to link scenario: integration payload is missing.");
            }

            return new Promise((resolve, reject) => {
                updateIntegrationMutation.mutate(
                    {
                        form_structure_id: int.form_structure_id,
                        custom_flow: int.custom_flow,
                        debug_mode: int.debug_mode,
                        use_new_connection_creator: int.use_new_connection_creator,
                        status: int.status,
                        name: int.name,
                        notes: int.notes,
                        owner_id: int.owner_id,
                        country_id: int.country_id,
                        scenario_ids: [newScenarioId],
                    },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({ queryKey: ["getIntegration", integrationId] });
                            resolve();
                        },
                        onError: reject,
                    }
                );
            });
        },
        [integrationId, updateIntegrationMutation]
    );

    if (isLoadingFreshIntegration || isLoadingCustomFields || isFreshIntegrationError) {
        return (
            <QueryState
                isLoading={isLoadingFreshIntegration || isLoadingCustomFields}
                isError={isFreshIntegrationError}
                errorKey="errors.loading_integration"
            />
        );
    }

    if (!integration?.id) {
        return null;
    }

    return (
        <TenantPage headerNavigationItems={() => getToolScenarioIntegrationHeaderItems()} isEdit>
            <SectionContentWrapper withBackground>
                <DiagramWithStorage
                    key={integrationId}
                    entityId={integrationId}
                    entityType={CCEntityType.Integration}
                    scenarioId={scenarioId}
                    onScenarioCreated={onScenarioCreated}
                    formFields={integration.formFields}
                    customFields={customFields}
                />
            </SectionContentWrapper>
        </TenantPage>
    );
}
